import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Divider,
  Avatar,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Close,
  Phone,
  Email,
  Message,
  LocationOn,
  CalendarMonth,
  Edit
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

interface CustomerDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  customerId: string | null;
  customerData: any;
}

const CustomerDetailDrawer: React.FC<CustomerDetailDrawerProps> = ({
  open,
  onClose,
  customerId,
  customerData
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Mock customer data - replace with real API call
  const customer = customerData || {
    name: 'John Doe',
    phone: '+1 (519) 804-0969',
    email: 'john.doe@email.com',
    status: 'active',
    customerSince: '2023-06-15',
    location: 'Toronto, ON',
    account: 'Premium',
    lifetimeValue: 1250,
    totalCalls: 24,
    totalMessages: 156,
    lastContact: new Date(Date.now() - 86400000 * 2),
    recentCalls: [
      { id: 1, date: new Date(Date.now() - 86400000), duration: 324, resolved: true, category: 'Billing' },
      { id: 2, date: new Date(Date.now() - 86400000 * 3), duration: 182, resolved: true, category: 'Technical' },
      { id: 3, date: new Date(Date.now() - 86400000 * 7), duration: 456, resolved: false, category: 'Sales' },
    ],
    sentimentHistory: [
      { date: 'Jan 1', score: 4.2 },
      { date: 'Jan 8', score: 4.5 },
      { date: 'Jan 15', score: 4.8 },
      { date: 'Jan 22', score: 4.6 },
      { date: 'Jan 29', score: 4.7 },
    ],
    notes: [
      { id: 1, date: new Date(Date.now() - 86400000), author: 'Agent Smith', text: 'Customer prefers email for follow-ups' },
      { id: 2, date: new Date(Date.now() - 86400000 * 5), author: 'Agent Jones', text: 'Resolved billing discrepancy' },
    ],
    openTickets: 2,
    resolvedTickets: 18,
  };

  const getSentimentColor = (score: number) => {
    if (score >= 4.5) return '#10B981';
    if (score >= 3.5) return '#F59E0B';
    return '#EF4444';
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 600, md: 700 },
          background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ height: '100%' }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0176D3 0%, #005FB2 100%)',
                p: 3,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
                }}
              />
              
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" position="relative">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'white',
                      color: '#0176D3',
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }}
                  >
                    {customer.name.split(' ').map((n: string) => n[0]).join('')}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {customer.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={customer.status.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(16, 185, 129, 0.2)',
                          color: 'white',
                          fontWeight: 700,
                          height: 22,
                        }}
                      />
                      <Chip
                        label={customer.account}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(251, 191, 36, 0.2)',
                          color: 'white',
                          fontWeight: 700,
                          height: 22,
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                  <Close />
                </IconButton>
              </Stack>

              {/* Quick Stats */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      {customer.totalCalls}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Total Calls
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      ${customer.lifetimeValue}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Lifetime Value
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  },
                }}
              >
                <Tab label="Overview" />
                <Tab label="History" />
                <Tab label="Notes" />
              </Tabs>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3, overflow: 'auto', height: 'calc(100% - 300px)' }}>
              {/* Overview Tab */}
              {activeTab === 0 && (
                <Stack spacing={3}>
                  {/* Contact Info Card */}
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
                        Contact Information
                      </Typography>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Phone sx={{ color: '#3B82F6' }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              Phone
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"SF Mono", monospace' }}>
                              {customer.phone}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Email sx={{ color: '#3B82F6' }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              Email
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {customer.email}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <LocationOn sx={{ color: '#3B82F6' }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              Location
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {customer.location}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <CalendarMonth sx={{ color: '#3B82F6' }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              Customer Since
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {format(new Date(customer.customerSince), 'MMM d, yyyy')}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Tickets Card */}
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
                        Support Tickets
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: '#FEF3C7',
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#92400E' }}>
                              {customer.openTickets}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600 }}>
                              Open Tickets
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: '#D1FAE5',
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#065F46' }}>
                              {customer.resolvedTickets}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#065F46', fontWeight: 600 }}>
                              Resolved
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Stack>
              )}

              {/* History Tab */}
              {activeTab === 1 && (
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', mb: 1 }}>
                    Recent Interactions
                  </Typography>
                  {customer.recentCalls.map((call: any) => (
                    <Card key={call.id} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: call.resolved ? '#D1FAE5' : '#FEE2E2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Phone sx={{ color: call.resolved ? '#065F46' : '#991B1B', fontSize: 20 }} />
                            </Box>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                {call.category} Support
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748B' }}>
                                {formatDistanceToNow(call.date, { addSuffix: true })}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip
                            label={call.resolved ? 'Resolved' : 'Pending'}
                            size="small"
                            sx={{
                              bgcolor: call.resolved ? '#D1FAE5' : '#FEE2E2',
                              color: call.resolved ? '#065F46' : '#991B1B',
                              fontWeight: 700,
                            }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                          <Chip
                            label={formatDuration(call.duration)}
                            size="small"
                            sx={{ fontFamily: '"SF Mono", monospace', fontWeight: 600 }}
                          />
                          <Chip label={format(call.date, 'MMM d, h:mm a')} size="small" />
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}

              {/* Notes Tab */}
              {activeTab === 2 && (
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                      Customer Notes
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Edit />}
                      sx={{
                        background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Add Note
                    </Button>
                  </Stack>
                  {customer.notes.map((note: any) => (
                    <Card key={note.id} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#3B82F6' }}>
                            {note.author.split(' ')[0][0]}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                {note.author}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748B' }}>
                                {formatDistanceToNow(note.date, { addSuffix: true })}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: '#475569' }}>
                              {note.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Quick Actions Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid rgba(0,0,0,0.08)',
                bgcolor: 'white',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Phone />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: '#3B82F6',
                      color: '#3B82F6',
                    }}
                  >
                    Call
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Email />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: '#3B82F6',
                      color: '#3B82F6',
                    }}
                  >
                    Email
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Message />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: '#3B82F6',
                      color: '#3B82F6',
                    }}
                  >
                    SMS
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Drawer>
  );
};

export default CustomerDetailDrawer;
